/**
 * Utility functions to load and parse alumni data from CSV or localStorage
 */
/**
 * Reformat name from "lastname firstname" to "firstname lastname"
 * @param {string} name - Name in "lastname firstname" format
 * @returns {string} - Name in "firstname lastname" format
 */
export const reformatName = (name) => {
  if (!name || typeof name !== 'string') return name;
  
  // Split the name by spaces
  const parts = name.trim().split(/\s+/);
  
  // If there's only one part or no parts, return as is
  if (parts.length <= 1) return name;
  
  // Move the first part (lastname) to the end
  const firstname = parts.slice(1).join(' ');
  const lastname = parts[0];
  
  return `${firstname} ${lastname}`;
};

/**
 * Load and parse a CSV file
 * @param {string} filePath - Path to the CSV file
 * @returns {Promise<Array>} - Promise that resolves to an array of objects representing CSV data
 */
export const loadCsvFile = async (filePath) => {
  try {
    console.log('Attempting to load CSV from:', filePath);
    const response = await fetch(filePath);
    
    if (!response.ok) {
      throw new Error(`Failed to load CSV: ${response.status} ${response.statusText}`);
    }
    
    const csvText = await response.text();
    console.log('CSV loaded successfully, length:', csvText.length);
    
    // Validate CSV content
    if (!csvText || csvText.trim() === '') {
      throw new Error('CSV file is empty');
    }
    
    const data = parseCSV(csvText);
    
    // Validate parsed data
    if (!data || data.length === 0) {
      throw new Error('No valid data found in CSV file');
    }
    
    // Reformat names in the parsed data
    data.forEach(item => {
      if (item.emp_empname) {
        item.original_empname = item.emp_empname; // Keep original for reference
        item.emp_empname = reformatName(item.emp_empname);
      }
    });
    
    return data;
  } catch (error) {
    console.error('Error loading CSV file:', error);
    throw error; // Re-throw the error to be handled by the caller
  }
};

/**
 * Parse CSV text into an array of objects
 * @param {string} csvText - CSV content as text
 * @returns {Array} - Array of objects representing CSV data
 */
export const parseCSV = (csvText) => {
  const lines = csvText.split('\n');
  if (lines.length === 0) return [];
  
  const headers = lines[0].split(',').map(header => header.trim());
  console.log('CSV Headers:', headers);
  
  const result = [];
  for (let i = 1; i < lines.length; i++) {
    if (lines[i].trim() === '') continue;
    
    // Handle quoted values with commas inside
    const values = [];
    let currentValue = '';
    let inQuotes = false;
    
    for (let j = 0; j < lines[i].length; j++) {
      const char = lines[i][j];
      
      if (char === '"' && (j === 0 || lines[i][j-1] !== '\\')) {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        values.push(currentValue.trim());
        currentValue = '';
      } else {
        currentValue += char;
      }
    }
    
    // Add the last value
    values.push(currentValue.trim());
    
    // Create object from headers and values
    const entry = {};
    headers.forEach((header, index) => {
      // Remove quotes if present
      let value = values[index] || '';
      if (value.startsWith('"') && value.endsWith('"')) {
        value = value.substring(1, value.length - 1);
      }
      
      // Handle special values
      if (value === 'NULL' || value === 'null') {
        value = '';
      }
      
      // Ensure all fields from the CSV are included
      entry[header] = value;
    });
    
    // Log the first entry to debug
    if (i === 1) {
      console.log('First CSV entry:', entry);
    }
    
    result.push(entry);
  }
  
  console.log(`Parsed ${result.length} records from CSV`);
  return result;
};

/**
 * Generate sample employee data for testing
 * @returns {Array} - Array of sample employee objects
 */
const getSampleData = () => {
  return [
    {
      emp_empcode: 'EMP001',
      emp_empname: 'John Smith', // Already in firstname lastname format
      original_empname: 'Smith John',
      emp_dateofbirth: '1980-05-15',
      emp_homephone: '555-123-4567',
      emp_workphone: '555-987-6543',
      emp_maritalstatus: 'Married',
      emp_startdate: '2015-03-10',
      emp_enddate: '2022-06-30',
      emp_address1: '123 Main Street',
      emp_address2: 'Apt 4B',
      emp_address3: 'Downtown',
      emp_address4: 'New York, NY 10001',
      emp_email: 'john.smith@example.com',
      emp_cellphone: '555-789-0123',
      emp_imm_status: 'Citizen',
      emp_sex: 'Male',
      emp_race: 'Caucasian',
      emp_country: 'USA',
      currentTitle: 'Senior Software Engineer',
      currentCompany: 'Tech Innovations Inc.',
      linkedInUrl: 'https://www.linkedin.com/in/john-smith-12345'
    },
    {
      emp_empcode: 'EMP002',
      emp_empname: 'Sarah Johnson', // Already in firstname lastname format
      original_empname: 'Johnson Sarah',
      emp_dateofbirth: '1985-08-22',
      emp_homephone: '555-234-5678',
      emp_workphone: '555-876-5432',
      emp_maritalstatus: 'Single',
      emp_startdate: '2017-01-15',
      emp_enddate: '2023-02-28',
      emp_address1: '456 Oak Avenue',
      emp_address2: 'Suite 302',
      emp_address3: 'Midtown',
      emp_address4: 'Chicago, IL 60601',
      emp_email: 'sarah.johnson@example.com',
      emp_cellphone: '555-678-9012',
      emp_imm_status: 'Permanent Resident',
      emp_sex: 'Female',
      emp_race: 'African American',
      emp_country: 'USA',
      currentTitle: 'CTO',
      currentCompany: 'TechCare Inc.',
      linkedInUrl: 'https://www.linkedin.com/in/sarah-johnson-67890'
    },
    {
      emp_empcode: 'EMP003',
      emp_empname: 'Michael Chang', // Already in firstname lastname format
      original_empname: 'Chang Michael',
      emp_dateofbirth: '1978-11-30',
      emp_homephone: '555-345-6789',
      emp_workphone: '555-765-4321',
      emp_maritalstatus: 'Married',
      emp_startdate: '2014-07-22',
      emp_enddate: '2022-12-15',
      emp_address1: '789 Pine Road',
      emp_address2: 'Building C',
      emp_address3: 'Tech District',
      emp_address4: 'San Francisco, CA 94105',
      emp_email: 'michael.chang@example.com',
      emp_cellphone: '555-567-8901',
      emp_imm_status: 'Citizen',
      emp_sex: 'Male',
      emp_race: 'Asian',
      emp_country: 'USA',
      currentTitle: 'VP of Engineering',
      currentCompany: 'Innovate Solutions',
      linkedInUrl: 'https://www.linkedin.com/in/michael-chang-24680'
    },
    {
      emp_empcode: 'EMP004',
      emp_empname: 'Emily Rodriguez', // Already in firstname lastname format
      original_empname: 'Rodriguez Emily',
      emp_dateofbirth: '1990-04-12',
      emp_homephone: '555-456-7890',
      emp_workphone: '555-654-3210',
      emp_maritalstatus: 'Single',
      emp_startdate: '2018-09-05',
      emp_enddate: '2023-05-20',
      emp_address1: '321 Maple Drive',
      emp_address2: 'Unit 5',
      emp_address3: 'Westside',
      emp_address4: 'Los Angeles, CA 90001',
      emp_email: 'emily.rodriguez@example.com',
      emp_cellphone: '555-456-7890',
      emp_imm_status: 'Citizen',
      emp_sex: 'Female',
      emp_race: 'Hispanic',
      emp_country: 'USA',
      currentTitle: 'Director of Product',
      currentCompany: 'Global Systems',
      linkedInUrl: 'https://www.linkedin.com/in/emily-rodriguez-13579'
    },
    {
      emp_empcode: 'EMP005',
      emp_empname: 'David Wilson', // Already in firstname lastname format
      original_empname: 'Wilson David',
      emp_dateofbirth: '1982-07-08',
      emp_homephone: '555-567-8901',
      emp_workphone: '555-543-2109',
      emp_maritalstatus: 'Divorced',
      emp_startdate: '2016-04-18',
      emp_enddate: '2023-01-10',
      emp_address1: '654 Cedar Lane',
      emp_address2: 'Apartment 7C',
      emp_address3: 'Financial District',
      emp_address4: 'Boston, MA 02110',
      emp_email: 'david.wilson@example.com',
      emp_cellphone: '555-345-6789',
      emp_imm_status: 'Citizen',
      emp_sex: 'Male',
      emp_race: 'Caucasian',
      emp_country: 'USA',
      currentTitle: 'CIO',
      currentCompany: 'Enterprise Tech',
      linkedInUrl: 'https://www.linkedin.com/in/david-wilson-97531'
    },
    {
      emp_empcode: 'EMP006',
      emp_empname: 'Jennifer Lee', // Already in firstname lastname format
      original_empname: 'Lee Jennifer',
      emp_dateofbirth: '1988-09-18',
      emp_homephone: '555-678-9012',
      emp_workphone: '555-432-1098',
      emp_maritalstatus: 'Married',
      emp_startdate: '2016-11-03',
      emp_enddate: '2023-04-15',
      emp_address1: '987 Elm Street',
      emp_address2: 'Suite 501',
      emp_address3: 'Tech Corridor',
      emp_address4: 'Seattle, WA 98101',
      emp_email: 'jennifer.lee@example.com',
      emp_cellphone: '555-234-5678',
      emp_imm_status: 'Citizen',
      emp_sex: 'Female',
      emp_race: 'Asian',
      emp_country: 'USA'
    },
    {
      emp_empcode: 'EMP007',
      emp_empname: 'Robert Martinez', // Already in firstname lastname format
      original_empname: 'Martinez Robert',
      emp_dateofbirth: '1975-12-05',
      emp_homephone: '555-789-0123',
      emp_workphone: '555-321-0987',
      emp_maritalstatus: 'Married',
      emp_startdate: '2013-08-12',
      emp_enddate: '2022-09-30',
      emp_address1: '753 Birch Avenue',
      emp_address2: 'Building 3',
      emp_address3: 'Innovation Park',
      emp_address4: 'Austin, TX 78701',
      emp_email: 'robert.martinez@example.com',
      emp_cellphone: '555-876-5432',
      emp_imm_status: 'Citizen',
      emp_sex: 'Male',
      emp_race: 'Hispanic',
      emp_country: 'USA'
    },
    {
      emp_empcode: 'EMP008',
      emp_empname: 'Michelle Thompson', // Already in firstname lastname format
      original_empname: 'Thompson Michelle',
      emp_dateofbirth: '1983-06-27',
      emp_homephone: '555-890-1234',
      emp_workphone: '555-210-9876',
      emp_maritalstatus: 'Single',
      emp_startdate: '2015-05-19',
      emp_enddate: '2022-11-25',
      emp_address1: '246 Willow Lane',
      emp_address2: 'Apartment 12D',
      emp_address3: 'Riverside',
      emp_address4: 'Denver, CO 80202',
      emp_email: 'michelle.thompson@example.com',
      emp_cellphone: '555-765-4321',
      emp_imm_status: 'Citizen',
      emp_sex: 'Female',
      emp_race: 'Caucasian',
      emp_country: 'USA'
    }
  ];
};

/**
 * Load employee data from localStorage or CSV file
 * @returns {Promise<Array>} - Promise that resolves to an array of employee objects
 */
const loadEmployees = async () => {
  try {
    // Try to get data from localStorage first
    const savedData = localStorage.getItem('alumni_data');
    
    if (savedData) {
      console.log('Loading alumni data from localStorage');
      return JSON.parse(savedData);
    }
    
    // If no data in localStorage, load from CSV
    console.log('No data in localStorage, loading from CSV');
    return await loadCsvFile('/data/former_employees.csv');
  } catch (error) {
    console.error('Error loading employee data:', error);
    return getSampleData();
  }
};

// Export the csvLoader object with all functions
export const csvLoader = {
  loadCsvFile,
  loadEmployees,
  reformatName
};
